import React, {
  FC,
  HTMLProps,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  cloneElement,
} from "react";
import cn from "classnames";
import "./index.module.scss";
import { TabPaneProps } from "./TabPane";
import stl from "./index.module.scss";

interface Props extends Omit<HTMLProps<HTMLDivElement>, "onChange"> {
  children: ReactNode;
  value?: string;
  onChange?: (v: string) => void;
  destroyHiddenTabs?: boolean;
}

const Tabs: FC<Props> = ({
  children,
  value,
  onChange,
  destroyHiddenTabs,
  ...props
}) => {
  const childrenArr = useMemo(() => {
    const childrenArrRaw = React.Children.toArray(
      children
    ) as ReactElement<TabPaneProps>[];
    return childrenArrRaw.filter(({ props }) => props.name);
  }, [children]);

  const [localValue, setLocalValue] = useState<string>();

  useEffect(() => {
    if (!value) {
      setLocalValue(childrenArr[0].props.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayValue = value ?? localValue;

  const onChanges = (v: string): void => {
    onChange?.(v);
    setLocalValue(v);
  };

  return (
    <div className={stl.tabs} {...props}>
      <ul className={stl.tabBtnList}>
        {childrenArr.map((child) => {
          const active = displayValue === child.props.name;
          const { disabled, name, title } = child.props;

          return (
            <li
              className={cn(stl.tabBtn, {
                [stl.active]: active,
                [stl.disabled]: disabled,
              })}
              key={name}
              tabIndex={!disabled ? 0 : undefined}
              onClick={() => {
                if (!disabled) onChanges(name);
              }}
              onKeyPress={({ key }) => {
                if (key === " " || key === "Enter") {
                  if (!disabled) onChanges(name);
                }
              }}
            >
              {title ?? name}
            </li>
          );
        })}
      </ul>

      {childrenArr.map((child) => {
        const active = displayValue === child.props.name;
        return cloneElement(child, {
          active,
          key: child.props.name,
          destroyIfHidden: destroyHiddenTabs,
        });
      })}
    </div>
  );
};

export default Tabs;
